import React from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { KeenIcon } from '@/components/keenicons';
import { useDeleteRole, useRoleUserCount } from '@/hooks/admin/useRoles';
import type { Role } from '@/services/admin/role.types';
import { toast } from 'sonner';

interface RolesTableProps {
  roles?: Role[];
  totalCount?: number;
  isLoading: boolean;
  error: Error | null;
  sortConfig: {
    column: 'name' | 'permissionCount' | 'userCount';
    direction: 'asc' | 'desc';
  };
  onSortChange: (column: 'name' | 'permissionCount' | 'userCount') => void;
  onRoleDeleted: () => void;
}

/**
 * Roles Table Component
 *
 * Displays roles in a sortable table with actions
 *
 * Features:
 * - Sortable columns (name, permission count, user count)
 * - Role badges (System, Default)
 * - Action menu (View, Edit, Delete)
 * - Links to role details and users
 * - Loading and error states
 *
 * Spec reference: Lines 172-257 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function RolesTable({
  roles,
  totalCount = 0,
  isLoading,
  error,
  sortConfig,
  onSortChange,
  onRoleDeleted,
}: RolesTableProps) {
  const deleteRoleMutation = useDeleteRole();

  const handleDelete = async (role: Role) => {
    if (role.isStatic) {
      toast.error('Cannot delete system role', {
        description: `The "${role.name}" role is a system role and cannot be deleted.`,
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteRoleMutation.mutateAsync(role.id);
      toast.success('Role deleted successfully', {
        description: `The role "${role.name}" has been removed.`,
      });
      onRoleDeleted();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      toast.error('Failed to delete role', {
        description: message,
      });
    }
  };

  const renderSortIcon = (column: typeof sortConfig.column) => {
    if (sortConfig.column !== column) {
      return <KeenIcon icon="sort" className="ml-2 text-muted-foreground" />;
    }
    return (
      <KeenIcon
        icon={sortConfig.direction === 'asc' ? 'arrow-up' : 'arrow-down'}
        className="ml-2"
      />
    );
  };

  if (isLoading) {
    return <TableSkeletonLoader />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <KeenIcon icon="information-circle" className="mb-4 text-4xl text-destructive" />
          <h3 className="mb-2 text-lg font-semibold">Failed to load roles</h3>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!roles || roles.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <KeenIcon icon="shield-cross" className="mb-4 text-4xl text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">No roles found</h3>
          <p className="text-sm text-muted-foreground">
            Try adjusting your search criteria or create a new role.
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalCountLabel = totalCount.toLocaleString();

  return (
    <Card>
      <CardContent className="p-0">
        <span className="sr-only">{totalCountLabel} roles total</span>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    onClick={() => onSortChange('name')}
                    className="flex items-center font-semibold hover:text-foreground"
                  >
                    Role Name
                    {renderSortIcon('name')}
                  </button>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="hidden md:table-cell">
                  <button
                    onClick={() => onSortChange('permissionCount')}
                    className="flex items-center font-semibold hover:text-foreground"
                  >
                    Permissions
                    {renderSortIcon('permissionCount')}
                  </button>
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  <button
                    onClick={() => onSortChange('userCount')}
                    className="flex items-center font-semibold hover:text-foreground"
                  >
                    Users
                    {renderSortIcon('userCount')}
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => {
                const descriptionValue = role.extraProperties?.description;
                const roleDescription =
                  typeof descriptionValue === 'string' ? descriptionValue : '';

                return (
                  <TableRow key={role.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/auth/roles/${role.id}`}
                            className="font-semibold hover:text-primary hover:underline"
                          >
                            {role.name}
                          </Link>
                          {role.isStatic && (
                            <Badge variant="secondary" className="text-xs">
                              <KeenIcon icon="shield-tick" className="mr-1 text-xs" />
                              System
                            </Badge>
                          )}
                          {role.isDefault && (
                            <Badge variant="outline" className="text-xs">
                              <KeenIcon icon="star" className="mr-1 text-xs" />
                              Default
                            </Badge>
                          )}
                        </div>
                        {/* Mobile: Show description on same row */}
                        <p className="text-sm text-muted-foreground md:hidden">
                          {roleDescription || '-'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {roleDescription || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <RolePermissionCount roleId={role.id} />
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <RoleUserCount roleId={role.id} />
                    </TableCell>
                    <TableCell className="text-right">
                      <RoleActionsDropdown role={role} onDelete={handleDelete} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Role Permission Count Component
 * Displays permission count with link to permissions tab
 */
function RolePermissionCount({ roleId }: { roleId: string }) {
  // TODO: This will be implemented when we add permission counting
  // For now, show placeholder
  return (
    <Link
      to={`/auth/roles/${roleId}#permissions`}
      className="text-sm text-primary hover:underline"
    >
      View Permissions
    </Link>
  );
}

/**
 * Role User Count Component
 * Fetches and displays user count for a role
 */
function RoleUserCount({ roleId }: { roleId: string }) {
  const { data: userCount, isLoading } = useRoleUserCount(roleId);

  if (isLoading) {
    return <span className="text-sm text-muted-foreground">Loading...</span>;
  }

  return (
    <Link
      to={`/auth/roles/${roleId}#users`}
      className="text-sm text-primary hover:underline"
    >
      {userCount?.toLocaleString() || 0} user{userCount !== 1 ? 's' : ''}
    </Link>
  );
}

/**
 * Role Actions Dropdown Component
 * Provides action menu for each role
 */
function RoleActionsDropdown({
  role,
  onDelete,
}: {
  role: Role;
  onDelete: (role: Role) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <KeenIcon icon="dots-vertical" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to={`/auth/roles/${role.id}`} className="flex items-center gap-2">
            <KeenIcon icon="eye" />
            View Details
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/auth/roles/${role.id}?edit=true`} className="flex items-center gap-2">
            <KeenIcon icon="pencil" />
            Edit Role
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/auth/roles/${role.id}#permissions`} className="flex items-center gap-2">
            <KeenIcon icon="shield-tick" />
            Manage Permissions
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDelete(role)}
          disabled={role.isStatic}
          className="text-destructive focus:text-destructive"
        >
          <KeenIcon icon="trash" className="mr-2" />
          Delete Role
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Table Skeleton Loader
 * Shows loading state while data is being fetched
 */
function TableSkeletonLoader() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="hidden md:table-cell">Permissions</TableHead>
                <TableHead className="hidden lg:table-cell">Users</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-5 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-5 w-48 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="h-5 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="h-5 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="ml-auto h-8 w-8 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
